import type { SupabaseClient } from '@supabase/supabase-js';

/**
 * Pagina una query Supabase hasta agotar resultados.
 *
 * Supabase aplica server-side cap `pgrst.db_max_rows` (default 1000) que
 * IGNORA el `.range(0, N)` del cliente cuando N excede el cap. La única
 * forma confiable de traer >1000 rows sin tocar config server es paginar
 * en lotes de tamaño <= cap.
 *
 * @param build callback que recibe (from, to) y construye la query con
 *   `.range(from, to)` aplicado al final. La query NO debe tener select
 *   final ejecutado, debe retornar el query builder con .range listo
 *   para await.
 *
 * @example
 *   const rows = await fetchAllPaginated<VParcelaTecnica>(
 *     (from, to) => sb.from('v_parcelas_tecnicas').select('*')
 *       .order('parcela_id', { ascending: true })
 *       .range(from, to),
 *     1000,
 *     20000,
 *   );
 */
export async function fetchAllPaginated<T>(
  build: (
    from: number,
    to: number,
  ) => PromiseLike<{ data: T[] | null; error: { message?: string } | null }>,
  pageSize = 1000,
  hardCap = 50_000,
): Promise<T[]> {
  if (pageSize < 1) throw new Error('pageSize must be >= 1');
  const out: T[] = [];
  let from = 0;
  while (out.length < hardCap) {
    const to = from + pageSize - 1;
    const { data, error } = await build(from, to);
    if (error) {
      const msg = (error as { message?: string }).message ?? JSON.stringify(error);
      throw new Error(msg);
    }
    const batch = data ?? [];
    if (batch.length === 0) break;
    out.push(...batch);
    if (batch.length < pageSize) break; // último page incompleto = fin
    from = to + 1;
  }
  return out;
}

/** Type-only narrowing helper para no romper tree-shaking. */
export type SbClient = SupabaseClient;
